import type { Middleware } from "@reduxjs/toolkit";

export const THEME_KEY_STORAGE = "themeKey";
export const THEME_KEY_CHANGE_EVENT = "themekey-change";

export enum ThemeOrgName {
  conceptwise = "conceptwise",
  earlypay = "earlypay",
}

export const THEME_MAP: Record<string, ThemeOrgName> = {
  org_01K8HFGM9TSJW822NT307VB7YH: ThemeOrgName.conceptwise,
  org_01JXH1F0SPB7CVFV17FXPV673F: ThemeOrgName.earlypay,
};

/**
 * Middleware that syncs the derived theme key to localStorage whenever
 * the user's workOSId changes. This allows useCustomThemePalette to read the
 * theme synchronously on first render, preventing an App re-render
 * cascade that causes double API calls.
 */
export const themeKeyMiddleware: Middleware = (store) => (next) => (action) => {
  const result = next(action);

  const storeState = store.getState();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const workOSId = (storeState as any).user?.organisation?.workOSId;

  // If user data hasn't loaded yet (e.g. during persist/REHYDRATE before
  // validateToken resolves), leave localStorage untouched so the cached
  // theme key from the previous session survives and useCustomThemePalette can
  // read it on the very first render — avoiding the undefined → themed
  // transition that re-renders App and triggers double API calls.
  if (!workOSId) return result;

  const themeKey = THEME_MAP[workOSId];
  const stored = localStorage.getItem(THEME_KEY_STORAGE);

  if (themeKey && stored !== themeKey) {
    localStorage.setItem(THEME_KEY_STORAGE, themeKey);
    window.dispatchEvent(new Event(THEME_KEY_CHANGE_EVENT));
  } else if (!themeKey && stored) {
    localStorage.removeItem(THEME_KEY_STORAGE);
    window.dispatchEvent(new Event(THEME_KEY_CHANGE_EVENT));
  }

  return result;
};
